import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Form, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { searchSchedules } from "../services/ProgramService";
import { fetchStates, fetchLanguages } from "../services/LocationService";
import LanguageData from "../models/LanguageData";
import { IMAGE_BASE } from "../Config";

interface ScheduleData {
  _id: string;
  programId: {
    _id: string;
    title: string;
    description: string;
    image1?: string;
    language: string;
    durationHours?: number;
    durationMinutes?: number;
  };
  startDate: string;
  endDate: string;
  timing: string;
  address: string;
  pinCode: string;
  state: string;
  country: string;
}

const SchedulesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [schedules, setSchedules] = useState<ScheduleData[]>([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("this");
  const [states, setStates] = useState<any[]>([]);
  const [languages, setLanguages] = useState<LanguageData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [dateRange, setDateRange] = useState<any>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    loadStates();
    loadLanguages();
    // Parse URL parameters
    const stateParam = searchParams.get('state') || "";
    const weekParam = searchParams.get('week') || "this";
    setSelectedState(stateParam);
    setSelectedWeek(weekParam);
  }, [searchParams]);

  useEffect(() => {
    if (states.length > 0) {
      performSearch();
    }
  }, [selectedState, selectedWeek, states]);



  const loadStates = async () => {
    try {
      const statesData = await fetchStates();
      setStates(statesData);
    } catch (error) {
      setError("Failed to load states");
    }
  };

  const loadLanguages = async () => {
    try {
      const languagesData = await fetchLanguages();
      setLanguages(languagesData);
    } catch (error) {
      setError("Failed to load languages");
    }
  };

  const performSearch = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await searchSchedules(selectedState, selectedWeek, 1, 50);
      setSchedules(response.schedules || []);
      setTotalResults(response.totalSchedules || 0);
      setDateRange(response.dateRange);
      
    } catch (err) {
      setError("Failed to load schedules");
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayClick = (playId: string) => {
    navigate(`/play/${playId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getWeekLabel = (week: string) => {
    switch (week) {
      case 'previous': return 'Previous Week';
      case 'next': return 'Next Week';
      default: return 'This Week';
    }
  };

  const formatDuration = (hours?: number, minutes?: number) => {
    if (!hours && !minutes) return "Duration TBA";
    const h = hours ? `${hours}h` : "";
    const m = minutes ? `${minutes}m` : "";
    return `${h} ${m}`.trim();
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', minHeight: '100vh' }}>
      <Container className="py-5">
        {/* Modern Header */}
        <div className="text-center mb-5">
          <div className="d-inline-flex align-items-center mb-3">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #FFD600 0%, #810081 100%)' }}
            >
              <i className="bi bi-calendar-event-fill text-white" style={{ fontSize: '1.5rem' }}></i>
            </div>
            <div>
              <h1 className="display-5 fw-bold mb-0" style={{ color: '#810081' }}>Show Schedules</h1>
              <p className="text-muted mb-0">Find shows happening near you</p>
            </div>
          </div>
        </div>

        {/* Modern Filters */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
          <Card.Body className="p-4">
            <Row className="g-3">
              <Col md={6}>
                <Form.Select 
                  value={selectedState} 
                  onChange={(e) => setSelectedState(e.target.value)}
                  disabled={loading}
                  className="py-3 border-0 bg-light"
                  style={{ borderRadius: '50px', fontSize: '16px' }}
                >
                  <option value="">All States</option>
                  {states.map((state) => (
                    <option key={state.stateCode} value={state.stateCode}>
                      {state.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Select 
                  value={selectedWeek} 
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  disabled={loading}
                  className="py-3 border-0 bg-light"
                  style={{ borderRadius: '50px', fontSize: '16px' }}
                >
                  <option value="previous">Previous Week</option>
                  <option value="this">This Week</option>
                  <option value="next">Next Week</option>
                </Form.Select>
              </Col>
            </Row>
            
            {/* Active Filters Pills */}
            {(selectedState || selectedWeek !== 'this') && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                <small className="text-muted me-2">Active filters:</small>
                {selectedState && (
                  <span 
                    className="badge d-flex align-items-center"
                    style={{ backgroundColor: '#810081', borderRadius: '50px', padding: '8px 12px' }}
                  >
                    State: {states.find(s => s.stateCode === selectedState)?.name}
                    <button 
                      className="btn-close btn-close-white ms-2" 
                      style={{ fontSize: '10px' }}
                      onClick={() => setSelectedState("")}
                    ></button>
                  </span>
                )}
                {selectedWeek !== 'this' && (
                  <span 
                    className="badge d-flex align-items-center"
                    style={{ backgroundColor: '#FFD600', color: '#810081', borderRadius: '50px', padding: '8px 12px' }}
                  >
                    Week: {getWeekLabel(selectedWeek)}
                    <button 
                      className="btn-close ms-2" 
                      style={{ fontSize: '10px' }}
                      onClick={() => setSelectedWeek('this')}
                    ></button>
                  </span>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Modern Results Info */}
        {!loading && dateRange && (
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="mb-1" style={{ color: '#810081' }}>
                {totalResults} Shows Found
              </h5>
              <small className="text-muted">
                {getWeekLabel(selectedWeek)}
                {selectedState && ` in ${states.find(s => s.stateCode === selectedState)?.name}`}
                <br />
                {new Date(dateRange.startDate).toLocaleDateString()} - {new Date(dateRange.endDate).toLocaleDateString()}
              </small>
            </div>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              className="rounded-pill"
              onClick={() => {
                const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                setSortOrder(newOrder);
                const sortedSchedules = [...schedules].sort((a, b) => {
                  return newOrder === 'asc' 
                    ? a.programId.title.localeCompare(b.programId.title)
                    : b.programId.title.localeCompare(a.programId.title);
                });
                setSchedules(sortedSchedules);
              }}
            >
              <i className={`bi bi-sort-alpha-${sortOrder === 'asc' ? 'down' : 'up'} me-1`}></i>
              Sort {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
            </Button>
          </div>
        )}

        {/* Modern Error Message */}
        {error && (
          <Alert 
            variant="" 
            className="border-0 text-center mb-4"
            style={{ 
              backgroundColor: 'rgba(255, 193, 7, 0.1)', 
              color: '#810081',
              borderRadius: '15px',
              padding: '20px'
            }}
          >
            <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: '#FFD600' }}></i>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#810081' }} />
            <p className="mt-3" style={{ color: '#810081' }}>Loading schedules...</p>
          </div>
        )}

        {/* Modern Schedules List */}
        {!loading && schedules.length > 0 && (
          <Row className="g-4">
            {schedules.map((schedule) => (
              <Col key={schedule._id} lg={6} xl={4}>
                <Card 
                  className="h-100 border-0" 
                  onClick={() => handlePlayClick(schedule.programId._id)}
                  style={{ 
                    cursor: "pointer", 
                    borderRadius: '20px',
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: '0 4px 20px rgba(129, 0, 129, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
                    e.currentTarget.style.boxShadow = "0 20px 40px rgba(129, 0, 129, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(129, 0, 129, 0.1)";
                  }}
                >
                <div className="position-relative overflow-hidden" style={{ borderRadius: '20px 20px 0 0' }}>
                  {schedule.programId.image1 ? (
                    <Card.Img 
                      variant="top" 
                      src={IMAGE_BASE + schedule.programId.image1} 
                      style={{ height: "200px", objectFit: "cover" }}
                    />
                  ) : (
                    <div 
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: "200px", background: "linear-gradient(135deg, #FFD600 0%, #810081 100%)" }}
                    >
                      <i className="bi bi-play-circle text-white" style={{ fontSize: "2.5rem" }}></i>
                    </div>
                  )}
                  <span 
                    className="badge position-absolute top-0 start-0 m-3"
                    style={{ backgroundColor: '#28a745', borderRadius: '50px', padding: '8px 12px' }}
                  >
                    {formatDate(schedule.startDate).split(',')[0]}
                  </span>
                </div>
                
                <Card.Body className="d-flex flex-column p-4">
                  <Card.Title className="fw-bold mb-2" style={{ color: '#810081', fontSize: '1.1rem' }}>
                    {schedule.programId.title}
                  </Card.Title>
                  
                  <Card.Text className="text-muted flex-grow-1" style={{ fontSize: "0.9rem" }}>
                    {schedule.programId.description && schedule.programId.description.length > 100 
                      ? `${schedule.programId.description.substring(0, 100)}...` 
                      : schedule.programId.description || "An exciting theatrical production."}
                  </Card.Text>
                  
                  <div className="mt-auto">
                    {/* Date and Time Pills */}
                    <div className="mb-3">
                      <span 
                        className="badge d-inline-flex align-items-center me-2 mb-2"
                        style={{ 
                          backgroundColor: 'rgba(129, 0, 129, 0.1)', 
                          color: '#810081', 
                          borderRadius: '50px',
                          padding: '6px 12px',
                          fontSize: '0.8rem'
                        }}
                      >
                        <i className="bi bi-calendar-event me-1"></i>
                        {formatDate(schedule.startDate)}
                      </span>
                      <span 
                        className="badge d-inline-flex align-items-center mb-2"
                        style={{ 
                          backgroundColor: 'rgba(255, 214, 0, 0.2)', 
                          color: '#810081', 
                          borderRadius: '50px',
                          padding: '6px 12px',
                          fontSize: '0.8rem'
                        }}
                      >
                        <i className="bi bi-clock me-1"></i>
                        {schedule.timing}
                      </span>
                    </div>
                    
                    {/* Venue Pill */}
                    <div className="mb-3">
                      <span 
                        className="badge d-inline-flex align-items-center"
                        style={{ 
                          backgroundColor: 'rgba(129, 0, 129, 0.1)', 
                          color: '#810081', 
                          borderRadius: '50px',
                          padding: '6px 12px',
                          fontSize: '0.8rem'
                        }}
                      >
                        <i className="bi bi-geo-alt me-1"></i>
                        {schedule.address}, {schedule.pinCode}
                      </span>
                    </div>
                    
                    {/* Duration and Language */}
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        {formatDuration(schedule.programId.durationHours, schedule.programId.durationMinutes)}
                      </small>
                      <span 
                        className="badge"
                        style={{ 
                          backgroundColor: 'rgba(255, 214, 0, 0.2)', 
                          color: '#810081', 
                          borderRadius: '50px',
                          padding: '4px 8px',
                          fontSize: '0.7rem',
                          fontWeight: '500'
                        }}
                      >
                        {languages.find(l => l.code === schedule.programId.language)?.nativeName || 
                         languages.find(l => l.code === schedule.programId.language)?.name || 
                         schedule.programId.language?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

        {/* Modern Empty State */}
        {!loading && schedules.length === 0 && !error && (
          <Card className="border-0 text-center py-5" style={{ borderRadius: '20px', background: 'rgba(255, 255, 255, 0.8)' }}>
            <Card.Body className="py-5">
              <div 
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'linear-gradient(135deg, rgba(255, 214, 0, 0.2) 0%, rgba(129, 0, 129, 0.2) 100%)' 
                }}
              >
                <i className="bi bi-calendar-x" style={{ fontSize: '2rem', color: '#810081' }}></i>
              </div>
              <h4 className="mb-3" style={{ color: '#810081' }}>No Shows Found</h4>
              <p className="text-muted mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>
                No shows are scheduled for {getWeekLabel(selectedWeek).toLowerCase()}
                {selectedState && ` in ${states.find(s => s.stateCode === selectedState)?.name}`}.
              </p>
              <Button 
                onClick={() => {
                  setSelectedState("");
                  setSelectedWeek("this");
                }}
                className="rounded-pill border-0 px-4 py-2"
                style={{ backgroundColor: '#FFD600', color: '#810081', fontWeight: '600' }}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                View All Shows This Week
              </Button>
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default SchedulesPage;